import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import StatusInsightModal from "./StatusInsightModal";
import { useLanguage } from "../contexts/LanguageContext";
import { getAuthHeaders } from "../utils/api";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// ======================
// SMART ORACLE ROUTING
// ======================
// Keywords to detect which Oracle to route to

const ORACLE_KEYWORDS = {
    // Dreams Oracle - mystical, subconscious, night visions
    dreams: {
        en: ['dream', 'dreams', 'dreaming', 'nightmare', 'sleep', 'subconscious', 'vision', 'visions', 'night', 'unconscious', 'symbolism', 'interpret'],
        es: ['sueño', 'sueños', 'soñar', 'pesadilla', 'dormir', 'subconsciente', 'visión', 'visiones', 'noche', 'inconsciente', 'simbolismo', 'interpretar']
    },
    // Love Oracle - relationships, emotions, heart matters
    love: {
        en: ['love', 'relationship', 'relationships', 'partner', 'romance', 'romantic', 'heart', 'soulmate', 'dating', 'marriage', 'breakup', 'ex', 'crush', 'attraction', 'intimacy', 'family', 'friend', 'friendship', 'connection', 'bond', 'bonds', 'emotion', 'emotional', 'feelings'],
        es: ['amor', 'relación', 'relaciones', 'pareja', 'romance', 'romántico', 'corazón', 'alma gemela', 'cita', 'matrimonio', 'ruptura', 'ex', 'atracción', 'intimidad', 'familia', 'amigo', 'amistad', 'conexión', 'vínculo', 'vínculos', 'emoción', 'emocional', 'sentimientos']
    }
};

/**
 * Detect the best Oracle based on command text keywords
 * @param {string} text - The command text to analyze
 * @param {string} language - 'en' or 'es'
 * @returns {string} 'cosmos' | 'vinculos' | 'suenos'
 */
const detectBestOracle = (text, language = 'en') => {
    if (!text) return 'cosmos';
    
    const lowerText = text.toLowerCase();
    const lang = language?.startsWith('es') ? 'es' : 'en';
    
    // Check for dreams keywords
    const dreamsKeywords = ORACLE_KEYWORDS.dreams[lang];
    for (const keyword of dreamsKeywords) {
        if (lowerText.includes(keyword)) {
            console.log(`[SmartRouting] Matched dreams keyword: "${keyword}" → Sueños Oracle`);
            return 'suenos';
        }
    }
    
    // Check for love keywords
    const loveKeywords = ORACLE_KEYWORDS.love[lang];
    for (const keyword of loveKeywords) {
        if (lowerText.includes(keyword)) {
            console.log(`[SmartRouting] Matched love keyword: "${keyword}" → Vínculos Oracle`);
            return 'vinculos';
        }
    }
    
    // Default to Cosmos (astrology, career, general guidance)
    console.log(`[SmartRouting] No specific match → Cosmos Oracle`);
    return 'cosmos';
};

/**
 * Get Oracle-specific button styling
 */
const getSmartOracleStyle = (text, language) => {
    const oracle = detectBestOracle(text, language);
    
    const styles = {
        cosmos: {
            bg: 'rgba(96,165,250,0.15)',
            bgEnd: 'rgba(96,165,250,0.05)',
            border: 'rgba(96,165,250,0.3)',
            borderHover: 'rgba(96,165,250,0.5)',
            color: '#60A5FA',
            icon: '✧'
        },
        vinculos: {
            bg: 'rgba(212,175,55,0.15)',
            bgEnd: 'rgba(212,175,55,0.05)',
            border: 'rgba(212,175,55,0.3)',
            borderHover: 'rgba(212,175,55,0.5)',
            color: '#D4AF37',
            icon: '♡'
        },
        suenos: {
            bg: 'rgba(147,51,234,0.15)',
            bgEnd: 'rgba(147,51,234,0.05)',
            border: 'rgba(147,51,234,0.3)',
            borderHover: 'rgba(147,51,234,0.5)',
            color: '#9333EA',
            icon: '☽'
        }
    };
    
    return styles[oracle] || styles.cosmos;
};

/**
 * Get Oracle-specific button label
 */
const getSmartOracleLabel = (text, language) => {
    const oracle = detectBestOracle(text, language);
    const isSpanish = language?.startsWith('es');
    
    const labels = {
        cosmos: isSpanish ? 'Consultar a Cosmos' : 'Ask Cosmos',
        vinculos: isSpanish ? 'Consultar a Vínculos' : 'Ask Bonds Oracle',
        suenos: isSpanish ? 'Consultar a Sueños' : 'Ask Dreams Oracle'
    };
    
    return labels[oracle] || labels.cosmos;
};

// Translation maps for moon phases and zodiac signs
const MOON_PHASE_TRANSLATIONS = {
    "New Moon": { en: "New Moon", es: "Luna Nueva" },
    "Waxing Crescent": { en: "Waxing Crescent", es: "Creciente" },
    "First Quarter": { en: "First Quarter", es: "Cuarto Creciente" },
    "Waxing Gibbous": { en: "Waxing Gibbous", es: "Gibosa Creciente" },
    "Full Moon": { en: "Full Moon", es: "Luna Llena" },
    "Waning Gibbous": { en: "Waning Gibbous", es: "Gibosa Menguante" },
    "Last Quarter": { en: "Last Quarter", es: "Cuarto Menguante" },
    "Waning Crescent": { en: "Waning Crescent", es: "Menguante" }
};

const ZODIAC_TRANSLATIONS = {
    "Aries": { en: "Aries", es: "Aries" },
    "Taurus": { en: "Taurus", es: "Tauro" },
    "Gemini": { en: "Gemini", es: "Géminis" },
    "Cancer": { en: "Cancer", es: "Cáncer" },
    "Leo": { en: "Leo", es: "Leo" },
    "Virgo": { en: "Virgo", es: "Virgo" },
    "Libra": { en: "Libra", es: "Libra" },
    "Scorpio": { en: "Scorpio", es: "Escorpio" },
    "Sagittarius": { en: "Sagittarius", es: "Sagitario" },
    "Capricorn": { en: "Capricorn", es: "Capricornio" },
    "Aquarius": { en: "Aquarius", es: "Acuario" },
    "Pisces": { en: "Pisces", es: "Piscis" }
};

/**
 * EnergyCapsule - Full-sized clickable pod with Gold (#D4AF37) border
 * RESTORED: All 4 metrics as prominent capsules
 */
const EnergyCapsule = ({ name, icon, level, color, onClick }) => {
    // Determine fill level for the capsule
    const fillPercent = Math.min(Math.max(level || 0, 0), 100);
    
    return (
        <motion.button
            className="relative flex flex-col items-center p-4 rounded-xl cursor-pointer"
            style={{
                background: 'linear-gradient(145deg, rgba(5,5,5,0.95) 0%, rgba(10,10,12,0.98) 100%)',
                border: '1px solid #D4AF37',
                boxShadow: '0 4px 20px rgba(0,0,0,0.3), 0 0 15px rgba(212,175,55,0.1)',
                minWidth: '80px'
            }}
            onClick={onClick}
            whileHover={{ 
                scale: 1.03, 
                boxShadow: '0 6px 30px rgba(0,0,0,0.4), 0 0 25px rgba(212,175,55,0.2)'
            }}
            whileTap={{ scale: 0.97 }}
        >
            {/* Capsule/Vial Shape */}
            <div className="relative w-10 h-20 rounded-full overflow-hidden mb-2"
                style={{
                    background: 'linear-gradient(180deg, rgba(5,5,5,0.9) 0%, rgba(15,15,18,0.95) 100%)',
                    border: '2px solid rgba(212,175,55,0.4)',
                    boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.5)'
                }}
            >
                {/* Fill Level */}
                <motion.div
                    className="absolute bottom-0 left-0 right-0"
                    style={{
                        background: `linear-gradient(0deg, ${color} 0%, ${color}80 100%)`,
                        boxShadow: `0 0 15px ${color}60, inset 0 2px 8px rgba(255,255,255,0.1)`
                    }}
                    initial={{ height: 0 }}
                    animate={{ height: `${fillPercent}%` }}
                    transition={{ duration: 1, delay: 0.2, ease: "easeOut" }}
                />
                
                {/* Glass reflection effect */}
                <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.08) 0%, transparent 50%)'
                    }}
                />
                
                {/* Bubble animation for high levels */}
                {fillPercent > 70 && (
                    <>
                        <motion.div
                            className="absolute w-1.5 h-1.5 rounded-full"
                            style={{ background: `${color}80`, left: '30%', bottom: '20%' }}
                            animate={{ y: [-5, -20], opacity: [0.8, 0] }}
                            transition={{ duration: 2, repeat: Infinity, delay: 0 }}
                        />
                        <motion.div
                            className="absolute w-1 h-1 rounded-full"
                            style={{ background: `${color}80`, left: '55%', bottom: '30%' }}
                            animate={{ y: [-3, -15], opacity: [0.6, 0] }}
                            transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                        />
                    </>
                )}
            </div>
            
            {/* Icon */}
            <span className="text-lg mb-1">{icon}</span>
            
            {/* Label */}
            <span className="text-[10px] font-cinzel text-primary/80 uppercase tracking-wide">
                {name}
            </span>
            
            {/* Percentage */}
            <span 
                className="text-xs font-inter mt-0.5"
                style={{ color: color }}
            >
                {fillPercent}%
            </span>
        </motion.button>
    );
};

/**
 * EnergyAccordion - Compact accordion view for energy metrics
 * Shows dominant energy as teaser, expands to reveal all 4
 */
const EnergyAccordion = ({ energyData, energyCapsules, onCapsuleClick, language, isExpanded, onToggle }) => {
    // Find the dominant (highest) energy
    const sortedCapsules = [...energyCapsules].sort((a, b) => b.level - a.level);
    const dominant = sortedCapsules[0];
    const avgEnergy = Math.round(energyCapsules.reduce((sum, c) => sum + c.level, 0) / energyCapsules.length);
    
    return (
        <motion.div
            className="rounded-xl overflow-hidden"
            style={{
                background: 'linear-gradient(145deg, rgba(5,5,5,0.95) 0%, rgba(10,10,12,0.98) 100%)',
                border: '1px solid rgba(212, 175, 55, 0.25)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
            }}
        >
            {/* Accordion Header - Always Visible */}
            <button
                onClick={onToggle}
                className="w-full flex items-center justify-between p-4 text-left group"
                data-testid="energy-accordion-toggle"
            >
                <div className="flex items-center gap-3">
                    <h2 className="text-xs font-cinzel text-primary/80 uppercase tracking-[0.2em] group-hover:text-primary transition-colors">
                        {language === 'es' ? 'Energía Interna' : 'Internal Energy'}
                    </h2>
                    <motion.span
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.3 }}
                        className="text-primary/40"
                    >
                        ▾
                    </motion.span>
                </div>
                
                {/* Collapsed Preview - Dominant Energy + Average */}
                {!isExpanded && (
                    <div className="flex items-center gap-3">
                        {/* Dominant metric pill */}
                        <div 
                            className="flex items-center gap-1.5 px-2.5 py-1 rounded-full"
                            style={{ 
                                background: `${dominant.color}15`,
                                border: `1px solid ${dominant.color}40`
                            }}
                        >
                            <span className="text-sm">{dominant.icon}</span>
                            <span className="text-xs font-inter" style={{ color: dominant.color }}>
                                {dominant.level}%
                            </span>
                        </div>
                        
                        {/* Average indicator */}
                        <span className="text-[10px] font-inter text-foreground/40">
                            {language === 'es' ? `Prom: ${avgEnergy}%` : `Avg: ${avgEnergy}%`}
                        </span>
                    </div>
                )}
            </button>
            
            {/* Expandable Content - Compact List */}
            <AnimatePresence>
                {isExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                    >
                        {/* Moon info */}
                        {energyData?.moon_sign && (
                            <div className="px-4 pb-2">
                                <span className="text-[9px] font-inter text-foreground/30">
                                    ☽ {ZODIAC_TRANSLATIONS[energyData.moon_sign]?.[language] || energyData.moon_sign} {MOON_PHASE_TRANSLATIONS[energyData.moon_phase]?.[language] || energyData.moon_phase}
                                </span>
                            </div>
                        )}
                        
                        {/* Compact Energy List */}
                        <div className="px-4 pb-4 space-y-2">
                            {energyCapsules.map((capsule, index) => (
                                <motion.button
                                    key={capsule.key}
                                    className="w-full flex items-center gap-3 p-3 rounded-lg"
                                    style={{
                                        background: 'rgba(5,5,5,0.6)',
                                        border: `1px solid ${capsule.color}20`
                                    }}
                                    onClick={() => onCapsuleClick(capsule)}
                                    initial={{ opacity: 0, x: -20 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    transition={{ delay: index * 0.05 }}
                                    whileHover={{ 
                                        scale: 1.01, 
                                        borderColor: `${capsule.color}40`,
                                        background: `${capsule.color}08`
                                    }}
                                    whileTap={{ scale: 0.99 }}
                                    data-testid={`energy-${capsule.key}`}
                                >
                                    {/* Icon */}
                                    <span className="text-xl w-8 text-center">{capsule.icon}</span>
                                    
                                    {/* Label */}
                                    <span className="text-xs font-cinzel text-foreground/80 uppercase tracking-wide flex-shrink-0 w-16">
                                        {capsule.name}
                                    </span>
                                    
                                    {/* Progress Bar */}
                                    <div className="flex-1 h-2 rounded-full bg-black/40 overflow-hidden">
                                        <motion.div
                                            className="h-full rounded-full"
                                            style={{ 
                                                background: `linear-gradient(90deg, ${capsule.color} 0%, ${capsule.color}80 100%)`,
                                                boxShadow: `0 0 8px ${capsule.color}40`
                                            }}
                                            initial={{ width: 0 }}
                                            animate={{ width: `${capsule.level}%` }}
                                            transition={{ duration: 0.8, delay: index * 0.1 }}
                                        />
                                    </div>
                                    
                                    {/* Percentage */}
                                    <span 
                                        className="text-sm font-inter font-medium w-12 text-right"
                                        style={{ color: capsule.color }}
                                    >
                                        {capsule.level}%
                                    </span>
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

/**
 * ActionItem - Streamlined command from the Oracle
 */
const ActionItem = ({ action, onClick }) => {
    const urgency = action.urgency || 'medium';
    const dotColor = urgency === 'high' ? '#F44336' : urgency === 'medium' ? '#D4AF37' : '#4CAF50';
    
    return (
        <motion.div 
            className="flex items-center gap-3 p-3 rounded-lg cursor-pointer"
            style={{
                background: 'rgba(5,5,5,0.6)',
                border: '1px solid rgba(212,175,55,0.15)'
            }}
            onClick={onClick}
            whileHover={{ 
                scale: 1.01, 
                borderColor: 'rgba(212,175,55,0.3)',
                background: 'rgba(212,175,55,0.05)'
            }}
            whileTap={{ scale: 0.99 }}
        >
            <span className="text-xl">{action.icon}</span>
            <div className="flex-1">
                <p className="text-sm font-inter text-foreground/90">{action.label}</p>
                {action.reasoning && (
                    <p className="text-[10px] font-inter text-foreground/40 mt-0.5 line-clamp-1">{action.reasoning}</p>
                )}
            </div>
            <motion.div
                className="w-2 h-2 rounded-full flex-shrink-0"
                style={{ background: dotColor, boxShadow: `0 0 8px ${dotColor}60` }}
                animate={{ scale: [1, 1.3, 1] }}
                transition={{ duration: 1.5, repeat: Infinity }}
            />
        </motion.div>
    );
};

/**
 * DailyCurrentCard - Energy Capsules RESTORED + Oracle Commands
 * 
 * DESIGN: 
 * - Full-sized Energy Capsules with Gold (#D4AF37) borders
 * - Obsidian (#050505) background
 * - All 4 metrics prominent
 */
export const DailyCurrentCard = ({ birthDate }) => {
    const { t, language } = useLanguage();
    const [energyData, setEnergyData] = useState(null);
    const [energyLoading, setEnergyLoading] = useState(false); // Start false - click to load
    const [energyError, setEnergyError] = useState(false);
    const [energyNotLoaded, setEnergyNotLoaded] = useState(true); // Track if never loaded
    const [energyExpanded, setEnergyExpanded] = useState(false); // Accordion state
    
    const [actionsData, setActionsData] = useState(null);
    const [actionsLoading, setActionsLoading] = useState(false);
    const [commandsExpanded, setCommandsExpanded] = useState(false);
    const [commandsFetched, setCommandsFetched] = useState(false);
    
    const [insightModal, setInsightModal] = useState({
        isOpen: false,
        category: null,
        displayName: null,
        score: null,
        reason: null
    });
    
    const [actionWhisper, setActionWhisper] = useState({ isOpen: false, action: null });

    // Cache helper functions - includes language in cache key
    const getCacheKey = (type) => `astrum_${type}_${birthDate || 'guest'}_${language}`;
    
    const isCacheValid = (cacheKey) => {
        const cached = localStorage.getItem(cacheKey);
        if (!cached) return false;
        try {
            const { timestamp } = JSON.parse(cached);
            const cachedDate = new Date(timestamp).toDateString();
            const today = new Date().toDateString();
            return cachedDate === today; // Valid if same day
        } catch {
            return false;
        }
    };
    
    const getFromCache = (cacheKey) => {
        try {
            const cached = localStorage.getItem(cacheKey);
            if (cached) {
                const { data } = JSON.parse(cached);
                return data;
            }
        } catch {}
        return null;
    };
    
    const saveToCache = (cacheKey, data) => {
        try {
            localStorage.setItem(cacheKey, JSON.stringify({
                data,
                timestamp: new Date().toISOString()
            }));
        } catch {}
    };

    // Listen for language changes and reset state to force re-fetch
    useEffect(() => {
        const handleLanguageChange = () => {
            console.log('[DailyCurrentCard] Language changed, resetting state');
            setEnergyData(null);
            setEnergyLoading(false);
            setEnergyError(false);
            setEnergyNotLoaded(true);
            setActionsData(null);
            setActionsLoading(true);
            setInsightModal({ isOpen: false, category: null, displayName: null, score: null, reason: null });
            setActionWhisper({ isOpen: false, action: null });
        };
        window.addEventListener('languageChanged', handleLanguageChange);
        return () => window.removeEventListener('languageChanged', handleLanguageChange);
    }, []);

    // Fetch dynamic actions from Oracle with language - ONLY when expanded
    useEffect(() => {
        if (!commandsExpanded || commandsFetched) return;
        
        const fetchActions = async () => {
            setActionsLoading(true);
            setCommandsFetched(true);
            try {
                const headers = await getAuthHeaders();
                const response = await fetch(`${API_URL}/api/actions/today?lang=${language}`, { 
                    headers,
                    credentials: "include" 
                });
                if (response.ok) {
                    const data = await response.json();
                    if (data) setActionsData(data);
                }
            } catch (err) {
                console.warn("Actions fetch error:", err);
            } finally {
                // KILL-SWITCH: Always stop loading spinner
                setActionsLoading(false);
            }
        };
        fetchActions();
    }, [commandsExpanded, commandsFetched, language]);

    // Check cache on mount for energy
    useEffect(() => {
        const cacheKey = getCacheKey('energy');
        if (isCacheValid(cacheKey)) {
            const cachedData = getFromCache(cacheKey);
            if (cachedData) {
                setEnergyData(cachedData);
                setEnergyNotLoaded(false);
            }
        }
    }, [birthDate]);

    // Fetch energy scores - ONLY when clicked
    // API KILL-SWITCH: Using try/catch/finally to ensure loading stops on any error
    const fetchEnergy = async () => {
        // Check cache first
        const cacheKey = getCacheKey('energy');
        if (isCacheValid(cacheKey)) {
            const cachedData = getFromCache(cacheKey);
            if (cachedData) {
                setEnergyData(cachedData);
                setEnergyNotLoaded(false);
                setEnergyLoading(false);
                return;
            }
        }
        
        setEnergyLoading(true);
        setEnergyError(false);
        setEnergyNotLoaded(false);
        
        try {
            // Try authenticated endpoint first with language param
            let data = null;
            let headers = await getAuthHeaders();
            let response = await fetch(`${API_URL}/api/energy/me?lang=${language}`, {
                method: "GET",
                headers,
                credentials: "include"
            });
            
            // If 401, retry with force-refreshed token
            if (response.status === 401) {
                console.log('[Energy] 401 received, refreshing token...');
                headers = await getAuthHeaders(true); // Force refresh
                response = await fetch(`${API_URL}/api/energy/me?lang=${language}`, {
                    method: "GET",
                    headers,
                    credentials: "include"
                });
            }
            
            if (response.ok) {
                data = await response.json();
            } else if (birthDate) {
                // Fallback to calculate endpoint if auth fails
                const fallbackResponse = await fetch(`${API_URL}/api/energy/calculate?lang=${language}`, {
                    method: "POST",
                    headers: { ...headers, "Content-Type": "application/json" },
                    credentials: "include",
                    body: JSON.stringify({ birth_date: birthDate })
                });
                if (fallbackResponse.ok) {
                    data = await fallbackResponse.json();
                }
            }
            
            if (data) {
                setEnergyData(data);
                saveToCache(cacheKey, data); // Cache the result
            } else {
                setEnergyError(true);
            }
        } catch {
            setEnergyError(true);
        } finally {
            // KILL-SWITCH: Always stop the loading spinner, even on 400/422 errors
            setEnergyLoading(false);
        }
    };

    // Energy capsule configurations
    const energyCapsules = [
        { key: 'love', name: t('dashboard.love'), icon: '💗', level: energyData?.love, color: '#E91E63', reason: energyData?.love_reason },
        { key: 'career', name: t('dashboard.career'), icon: '💼', level: energyData?.career, color: '#4CAF50', reason: energyData?.career_reason },
        { key: 'health', name: t('dashboard.health'), icon: '🌿', level: energyData?.health, color: '#2196F3', reason: energyData?.health_reason },
        { key: 'spirit', name: t('dashboard.spirit'), icon: '✨', level: energyData?.spirit, color: '#9C27B0', reason: energyData?.spirit_reason }
    ];

    return (
        <motion.div 
            className="w-full px-4 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
        >
            {/* ENERGY ACCORDION - Compact with dominant energy teaser */}
            {energyNotLoaded && !energyLoading ? (
                // Initial "Tap to reveal" state
                <motion.div 
                    className="rounded-xl p-5 mb-4 cursor-pointer"
                    style={{
                        background: 'linear-gradient(145deg, rgba(5,5,5,0.95) 0%, rgba(10,10,12,0.98) 100%)',
                        border: '1px dashed rgba(212,175,55,0.25)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                    }}
                    onClick={fetchEnergy}
                    whileHover={{ scale: 1.01, borderColor: 'rgba(212,175,55,0.4)' }}
                    whileTap={{ scale: 0.99 }}
                    data-testid="energy-reveal-btn"
                >
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs font-cinzel text-primary/80 uppercase tracking-[0.2em]">
                            {language === 'es' ? 'Energía Interna' : 'Internal Energy'}
                        </h2>
                        <div className="flex items-center gap-2">
                            <motion.span 
                                className="text-lg"
                                animate={{ opacity: [0.5, 1, 0.5] }}
                                transition={{ duration: 2, repeat: Infinity }}
                            >
                                ✧
                            </motion.span>
                            <span className="text-[10px] font-inter text-primary/40">
                                {language === 'es' ? 'Toca para revelar' : 'Tap to reveal'}
                            </span>
                        </div>
                    </div>
                </motion.div>
            ) : energyLoading ? (
                // Loading state
                <div 
                    className="rounded-xl p-5 mb-4"
                    style={{
                        background: 'linear-gradient(145deg, rgba(5,5,5,0.95) 0%, rgba(10,10,12,0.98) 100%)',
                        border: '1px solid rgba(212,175,55,0.25)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                    }}
                >
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs font-cinzel text-primary/80 uppercase tracking-[0.2em]">
                            {language === 'es' ? 'Energía Interna' : 'Internal Energy'}
                        </h2>
                        <motion.span 
                            className="text-lg"
                            animate={{ rotate: 360 }}
                            transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                        >
                            ✧
                        </motion.span>
                    </div>
                    <div className="mt-4 space-y-2">
                        {[0, 1, 2, 3].map((i) => (
                            <motion.div 
                                key={i}
                                className="h-10 rounded-lg"
                                style={{ background: 'rgba(212,175,55,0.05)' }}
                                animate={{ opacity: [0.3, 0.6, 0.3] }}
                                transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.1 }}
                            />
                        ))}
                    </div>
                </div>
            ) : energyError || !energyData ? (
                // Error state
                <motion.div 
                    className="rounded-xl p-5 mb-4 cursor-pointer"
                    style={{
                        background: 'linear-gradient(145deg, rgba(5,5,5,0.95) 0%, rgba(10,10,12,0.98) 100%)',
                        border: '1px solid rgba(212,175,55,0.15)',
                        boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                    }}
                    onClick={fetchEnergy}
                    whileHover={{ scale: 1.01 }}
                >
                    <div className="flex items-center justify-between">
                        <h2 className="text-xs font-cinzel text-primary/80 uppercase tracking-[0.2em]">
                            {language === 'es' ? 'Energía Interna' : 'Internal Energy'}
                        </h2>
                        <span className="text-lg">🔮</span>
                    </div>
                    <p className="text-xs text-foreground/50 mt-3 text-center">{t('dashboard.oracle_meditating')}</p>
                    <p className="text-[10px] text-primary/40 mt-1 text-center">
                        {language === 'es' ? 'Toca para intentar de nuevo' : 'Tap to try again'}
                    </p>
                </motion.div>
            ) : (
                // Loaded state - Use Accordion
                <div className="mb-4">
                    <EnergyAccordion
                        energyData={energyData}
                        energyCapsules={energyCapsules}
                        language={language}
                        isExpanded={energyExpanded}
                        onToggle={() => setEnergyExpanded(!energyExpanded)}
                        onCapsuleClick={(capsule) => setInsightModal({
                            isOpen: true,
                            category: capsule.key,
                            displayName: capsule.name,
                            score: capsule.level,
                            reason: capsule.reason
                        })}
                    />
                </div>
            )}

            {/* TODAY'S COMMANDS - COLLAPSIBLE */}
            <motion.div 
                className="rounded-xl overflow-hidden"
                style={{
                    background: 'linear-gradient(145deg, rgba(5,5,5,0.95) 0%, rgba(10,10,12,0.98) 100%)',
                    border: '1px solid rgba(212, 175, 55, 0.2)',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.4)'
                }}
            >
                {/* Collapsible Header */}
                <button
                    onClick={() => setCommandsExpanded(!commandsExpanded)}
                    className="w-full flex items-center justify-between p-4 text-left group"
                >
                    <div className="flex items-center gap-2">
                        <h2 className="text-xs font-cinzel text-primary/80 uppercase tracking-[0.2em] group-hover:text-primary transition-colors">
                            {t('dashboard.todays_commands')}
                        </h2>
                        <motion.span
                            animate={{ rotate: commandsExpanded ? 180 : 0 }}
                            transition={{ duration: 0.3 }}
                            className="text-primary/40"
                        >
                            ▾
                        </motion.span>
                    </div>
                    {!commandsExpanded && (
                        <span className="text-primary/30 text-[10px] font-inter">
                            {language === 'es' ? 'Toca para revelar' : 'Tap to reveal'}
                        </span>
                    )}
                </button>
                
                {/* Expandable Content */}
                <AnimatePresence>
                    {commandsExpanded && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="overflow-hidden px-4 pb-4"
                        >
                            {actionsData?.transit_context && (
                                <span className="text-[9px] font-inter text-foreground/30 block mb-3">
                                    {actionsData.transit_context}
                                </span>
                            )}
                            
                            <div className="space-y-2">
                                {actionsLoading ? (
                                    <div className="flex flex-col gap-2">
                                        {[0, 1].map((i) => (
                                            <motion.div 
                                                key={i}
                                                className="h-14 rounded-lg flex items-center justify-center gap-3"
                                                style={{ 
                                                    background: 'linear-gradient(135deg, rgba(20,18,28,0.9) 0%, rgba(12,10,18,0.95) 100%)',
                                                    border: '1px solid rgba(212,175,55,0.1)'
                                                }}
                                                initial={{ opacity: 0, x: -20 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: i * 0.15 }}
                                            >
                                                <motion.span
                                                    animate={{ opacity: [0.3, 0.7, 0.3] }}
                                                    transition={{ duration: 1.5, repeat: Infinity, delay: i * 0.2 }}
                                                >
                                                    ✧
                                                </motion.span>
                                                <motion.div 
                                                    className="w-24 h-2 rounded-full bg-primary/15"
                                                    animate={{ opacity: [0.3, 0.5, 0.3] }}
                                                    transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.1 }}
                                                />
                                            </motion.div>
                                        ))}
                                    </div>
                                ) : actionsData?.actions ? (
                                    <>
                                        {actionsData.actions[0] && (
                                            <ActionItem 
                                                action={actionsData.actions[0]} 
                                                onClick={() => setActionWhisper({ isOpen: true, action: actionsData.actions[0] })}
                                            />
                                        )}
                                        {actionsData.actions[1] && (
                                            <ActionItem 
                                                action={actionsData.actions[1]} 
                                                onClick={() => setActionWhisper({ isOpen: true, action: actionsData.actions[1] })}
                                            />
                                        )}
                                    </>
                                ) : (
                                    <p className="text-sm text-foreground/40 text-center py-6 italic">
                                        {language === 'es' ? 'Esperando la sabiduría del Oráculo...' : 'Awaiting the Oracle\'s wisdom...'}
                                    </p>
                                )}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>

            {/* Insight Modal */}
            <StatusInsightModal
                isOpen={insightModal.isOpen}
                onClose={() => setInsightModal({ isOpen: false, category: null, displayName: null, score: null, reason: null })}
                category={insightModal.category}
                displayName={insightModal.displayName}
                score={insightModal.score}
                reason={insightModal.reason}
            />

            {/* Action Whisper Modal */}
            <AnimatePresence>
                {actionWhisper.isOpen && actionWhisper.action && (
                    <motion.div
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={() => setActionWhisper({ isOpen: false, action: null })}
                    >
                        <motion.div
                            className="max-w-sm w-full rounded-xl p-6"
                            style={{
                                background: 'linear-gradient(145deg, rgba(5,5,5,0.98) 0%, rgba(10,10,12,1) 100%)',
                                border: '1px solid rgba(212, 175, 55, 0.3)',
                                boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
                            }}
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.9, y: 20 }}
                            onClick={e => e.stopPropagation()}
                        >
                            <div className="text-center">
                                <span className="text-4xl mb-4 block">{actionWhisper.action.icon}</span>
                                <h3 className="text-lg font-cinzel text-primary mb-2">
                                    {actionWhisper.action.label}
                                </h3>
                                {actionWhisper.action.reasoning && (
                                    <p className="text-sm font-inter text-foreground/60 leading-relaxed mb-4">
                                        {actionWhisper.action.reasoning}
                                    </p>
                                )}
                                <div className="flex justify-center gap-2">
                                    <span className={`text-xs px-3 py-1 rounded-full ${
                                        actionWhisper.action.urgency === 'high' 
                                            ? 'bg-red-500/20 text-red-400'
                                            : actionWhisper.action.urgency === 'medium'
                                            ? 'bg-yellow-500/20 text-yellow-400'
                                            : 'bg-green-500/20 text-green-400'
                                    }`}>
                                        {language === 'es' 
                                            ? `${actionWhisper.action.urgency === 'high' ? 'alta' : actionWhisper.action.urgency === 'medium' ? 'media' : 'baja'} prioridad`
                                            : `${actionWhisper.action.urgency} priority`}
                                    </span>
                                </div>
                            </div>
                            
                            {/* Ask the Oracle Button - UX Bridge with Smart Routing */}
                            <motion.button
                                className="w-full mt-4 py-3 px-4 rounded-lg font-cinzel text-sm flex items-center justify-center gap-2"
                                style={{
                                    background: `linear-gradient(135deg, ${getSmartOracleStyle(actionWhisper.action.label, language).bg} 0%, ${getSmartOracleStyle(actionWhisper.action.label, language).bgEnd} 100%)`,
                                    border: `1px solid ${getSmartOracleStyle(actionWhisper.action.label, language).border}`,
                                    color: getSmartOracleStyle(actionWhisper.action.label, language).color
                                }}
                                whileHover={{ scale: 1.02, borderColor: getSmartOracleStyle(actionWhisper.action.label, language).borderHover }}
                                whileTap={{ scale: 0.98 }}
                                onClick={() => {
                                    // Smart Oracle Routing based on command keywords
                                    const oracleQuery = actionWhisper.action.label;
                                    const smartOracle = detectBestOracle(oracleQuery, language);
                                    
                                    window.dispatchEvent(new CustomEvent('openOracleWithQuery', {
                                        detail: { 
                                            query: oracleQuery,
                                            oracle: smartOracle
                                        }
                                    }));
                                    setActionWhisper({ isOpen: false, action: null });
                                }}
                                data-testid="ask-oracle-btn"
                            >
                                <span>{getSmartOracleStyle(actionWhisper.action.label, language).icon}</span>
                                <span>{getSmartOracleLabel(actionWhisper.action.label, language)}</span>
                                <span>{getSmartOracleStyle(actionWhisper.action.label, language).icon}</span>
                            </motion.button>
                            
                            <button
                                className="w-full mt-3 py-3 text-sm font-cinzel text-primary/60 border-t border-primary/10 hover:text-primary transition-colors"
                                onClick={() => setActionWhisper({ isOpen: false, action: null })}
                            >
                                {language === 'es' ? 'CERRAR' : 'CLOSE'}
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    );
};

export default DailyCurrentCard;
